/**
 * The browser's `window.api`.
 *
 * In the desktop project this object was the preload bridge: the entire
 * surface the sandboxed renderer could reach, every call crossing into the
 * main process. Here it has exactly the same shape, so the renderer copied
 * from the desktop calls it unchanged, and each call is answered in the page
 * by the File System Access API, IndexedDB and localStorage.
 *
 * One group is new: `web`, for what only a browser needs, such as asking the
 * user to let the page use a file again after a reload.
 */
import type {
  BackupInfo,
  DirEntry,
  DocumentFile,
  ExportPayload,
  ExportResult,
  FileProperties,
  HelpDoc,
  JournalEntry,
  MarkdownFile,
  PreviewFile,
  ReplaceResult,
  SaveImageRequest,
  SaveImageResult,
  SaveRequest,
  SaveResult,
  SearchFlags,
  SearchHit,
  ThemeInfo,
  VersionInfo,
  WatchEvent,
  WindowState,
} from './types'
import type { SettingsPatch, SettingsState } from '../shared/settings'
import type { ReplaceSpec } from '../shared/text-search'
import { ask, info } from './dialogs'
import {
  addRecentFile,
  openCopies,
  openDialog,
  pickWithInput,
  readDocument,
  saveAsDialog,
  saveDocument,
} from './files'
import {
  backupInfo,
  claimTab,
  clearJournal,
  flushJournals,
  flushJournalsAndWait,
  journal,
  listVersions,
  liveTabs,
  moveHistoryUnder,
  pendingRecoveries,
  readVersion,
} from './recovery'
import { decodeText } from './textfile'
import { getSettings, onSettingsChanged, patchSettings } from './settings'
import { explainSaveError } from '../shared/save-errors'
import {
  allMarkdown,
  applyReplace,
  createFile,
  createFolder,
  deleteDocument,
  initWorkspace,
  moveDocument,
  openDroppedFolder,
  openFolderAt,
  openFolderDialog,
  previewReplace,
  properties,
  readDir,
  rename,
  setWorkspace,
  trash,
} from './workspace'
import { onWatchEvents } from './watch'
import { cancelSearch, onDone, onHit, startSearch } from './search'
import { resolveImage, saveImage } from './images'
import { helpTopic } from './help'
import { exportHtml, exportPdf, printExport } from './export'
import { importTheme, listThemes, onThemesChanged, readTheme, removeTheme } from './themes'
import { writeClipboard } from './clipboard'
import { initLaunchQueue, onLaunchFile, takeLaunchFiles } from './launch'
import { ensurePermission, handleRecord, hasFileSystemAccess, pruneHandles } from './handles'
import { displayPath, parsePath } from './paths'
import { readDrop, dropHasOpenable, type Dropped } from './drop'

declare const __APP_VERSION__: string

export type EditAction =
  | 'undo'
  | 'redo'
  | 'cut'
  | 'copy'
  | 'paste'
  | 'pastePlain'
  | 'selectAll'
  | 'delete'

let editHandler: ((action: EditAction) => void) | null = null

/** Errors from the page, kept here only: nothing is ever sent anywhere. */
const errorLog: string[] = []

function subscribe<T extends unknown[]>(
  set: Set<(...args: T) => void>,
  fn: (...args: T) => void
): () => void {
  set.add(fn)
  return () => set.delete(fn)
}

const darkQuery =
  typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null

/** Whether the page may use a stored handle now, without asking. */
async function permitted(path: string): Promise<boolean> {
  const p = parsePath(path)
  if (!p || (p.scheme !== 'ws' && p.scheme !== 'fh')) return true
  const rec = await handleRecord(p.id)
  if (!rec) return true // Gone: reading it will say so.
  try {
    await ensurePermission(rec.handle, 'read', { ask: false })
    return true
  } catch {
    return false
  }
}

function createApi() {
  const focusListeners = new Set<(s: WindowState) => void>()
  const sendState = (): void => {
    const s = { maximized: false, focused: document.hasFocus() }
    for (const fn of focusListeners) fn(s)
  }
  window.addEventListener('focus', sendState)
  window.addEventListener('blur', sendState)

  const darkListeners = new Set<(dark: boolean) => void>()
  darkQuery?.addEventListener('change', (e) => {
    for (const fn of darkListeners) fn(e.matches)
  })

  return {
    file: {
      openDialog: (): Promise<DocumentFile[] | null> => openDialog(),
      read: (path: string): Promise<DocumentFile> => readDocument(path),
      save: (req: SaveRequest): Promise<SaveResult> => saveDocument(req),
      saveAsDialog: (suggested?: string): Promise<string | null> => saveAsDialog(suggested),
      /** A browser cannot show a file in its folder. */
      showInFolder: async (_path: string): Promise<void> => {},
      confirmClose: async (names: string[]): Promise<'save' | 'discard' | 'cancel'> => {
        const list = names.length === 1 ? `“${names[0]}”` : `${names.length} documents`
        const r = await ask({
          kind: 'warning',
          message: `Save changes to ${list}?`,
          detail: 'Your changes will be lost if you don’t save them.',
          buttons: ['Save', 'Don’t Save', 'Cancel'],
          defaultId: 0,
          cancelId: 2,
        })
        return (['save', 'discard', 'cancel'] as const)[r]
      },
      confirmReload: async (name: string): Promise<boolean> =>
        (await ask({
          kind: 'warning',
          message: `“${name}” was changed by another program.`,
          detail:
            'You have unsaved changes to it. Keep My Version leaves your edits open, and saving will replace the file. Reload from Disk discards your edits and shows the other version.',
          buttons: ['Keep My Version', 'Reload from Disk'],
          defaultId: 0,
          cancelId: 0,
        })) === 1,
      confirmOverwrite: async (name: string): Promise<boolean> =>
        (await ask({
          kind: 'warning',
          message: `“${name}” has changed on disk since you opened it.`,
          detail:
            'Overwrite replaces the version on disk with yours. Cancel keeps both as they are; your changes stay open in the editor.',
          buttons: ['Overwrite', 'Cancel'],
          defaultId: 1,
          cancelId: 1,
        })) === 0,
      reportSaveError: async (
        name: string,
        code: string | undefined,
        message: string
      ): Promise<void> => {
        const { summary, advice } = explainSaveError(code, message)
        await info(
          `“${name}” was not saved. ${summary}`,
          `${advice}\n\nYour changes are still open in the editor and have not been lost.`,
          'error'
        )
      },
      /** Fire and forget: journalling must never block a keystroke. */
      journal: (path: string, content: string): void => journal(path, content),
      pendingRecoveries: (): Promise<JournalEntry[]> =>
        pendingRecoveries(async (entry) => {
          if (entry.path.startsWith('untitled:')) return null
          if (!(await permitted(entry.path))) return null
          try {
            return (await readDocument(entry.path, { recent: false })).content === entry.content
          } catch {
            return null
          }
        }),
      backupInfo: (path: string): Promise<BackupInfo> => backupInfo(path),
      historyMoved: (from: string, to: string): Promise<void> => moveHistoryUnder(from, to),
      versions: (path: string): Promise<VersionInfo[]> => listVersions(path),
      versionContent: async (path: string, id: string): Promise<string | null> => {
        const bytes = await readVersion(path, id)
        return bytes ? decodeText(bytes).content : null
      },
      discardRecovery: (path: string): Promise<void> => clearJournal(path),
      recoveryPrompt: async (
        labels: string[]
      ): Promise<'restore' | 'later' | 'review' | 'discard'> => {
        const several = labels.length > 1
        const r = await ask({
          kind: 'question',
          message: 'Unsaved changes were recovered',
          detail: several
            ? `${labels.join('\n')}\n\nRestore them? Not Now keeps them, to be offered again.`
            : `${labels[0]}\n\nRestore the recovered version? Not Now keeps it, to be offered again.`,
          buttons: several
            ? ['Restore All', 'Not Now', 'Review…']
            : ['Restore', 'Not Now', 'Discard…'],
          defaultId: 0,
          cancelId: 1,
        })
        if (r === 0) return 'restore'
        if (r !== 2) return 'later'
        if (several) return 'review'
        const sure = await ask({
          kind: 'warning',
          message: 'Discard the recovered changes?',
          detail: `${labels[0]}\n\nThey cannot be recovered again.`,
          buttons: ['Keep Them', 'Discard'],
          defaultId: 0,
          cancelId: 0,
        })
        return sure === 1 ? 'discard' : 'later'
      },
      onOpenPath: (fn: (path: string) => void) => onLaunchFile(fn),
      /** Desktop only: a dropped file's place on disk. See `web.readDrop`. */
      pathForFile: (_file: File): string => '',
      /**
       * Files the app was opened with, and whether this tab reopens the last
       * session: only when it is the only tab of the app, as only the launch's
       * first window did on the desktop.
       */
      takePendingPaths: async (): Promise<{ paths: string[]; restoreSession: boolean }> => {
        await claimTab()
        const paths = takeLaunchFiles()
        return { paths, restoreSession: (await liveTabs()).size <= 1 }
      },
    },

    workspace: {
      openDialog: (): Promise<string | null> => openFolderDialog(),
      current: async (): Promise<string | null> => getSettings().workspace,
      set: (root: string | null): Promise<string | null> => setWorkspace(root),
      readDir: (dir: string): Promise<DirEntry[]> => readDir(dir),
      allMarkdown: async (root?: string): Promise<MarkdownFile[]> => {
        const open = getSettings().workspace
        return open ? allMarkdown(root ?? open) : []
      },
      onWatchEvents: (fn: (events: WatchEvent[]) => void) => onWatchEvents(fn),
    },

    search: {
      start: (query: string, opts: SearchFlags = {}): Promise<number> => startSearch(query, opts),
      cancel: async (): Promise<void> => cancelSearch(),
      previewReplace: (spec: ReplaceSpec, open: string[]): Promise<PreviewFile[]> =>
        previewReplace(spec, open),
      applyReplace: (
        spec: ReplaceSpec,
        files: Array<{ path: string; mtimeMs: number }>
      ): Promise<ReplaceResult> => applyReplace(spec, files),
      onHit: (fn: (payload: { id: number; hit: SearchHit }) => void) => onHit(fn),
      onDone: (fn: (payload: { id: number; found: number }) => void) => onDone(fn),
    },

    edit: {
      /** Menu-driven editing actions, carried out by the editor on screen. */
      action: (name: EditAction): void => editHandler?.(name),
    },

    images: {
      save: (req: SaveImageRequest): Promise<SaveImageResult> => saveImage(req),
    },

    help: {
      topic: (id: string): Promise<HelpDoc | null> => helpTopic(id),
    },

    export: {
      html: (payload: ExportPayload): Promise<ExportResult> => exportHtml(payload),
      pdf: (payload: ExportPayload): Promise<ExportResult> => exportPdf(payload, getSettings().pdf),
      print: (payload: ExportPayload): Promise<ExportResult> =>
        printExport(payload, getSettings().pdf),
      /** A browser cannot open a file it saved. */
      open: async (_path: string): Promise<string> => 'A browser cannot open a file from here.',
    },

    fileops: {
      properties: (path: string): Promise<FileProperties> => properties(path),
      move: (path: string): Promise<string | null> => moveDocument(path),
      delete: (path: string): Promise<boolean> => deleteDocument(path),
      parentDir: async (path: string): Promise<string> => path.slice(0, path.lastIndexOf('/')),
      createFile: (dir: string, name: string): Promise<string> => createFile(dir, name),
      createFolder: (dir: string, name: string): Promise<string> => createFolder(dir, name),
      rename: (path: string, name: string): Promise<string> => rename(path, name),
      trash: (path: string): Promise<boolean> => trash(path),
    },

    settings: {
      get: async (): Promise<SettingsState> => getSettings(),
      patch: async (patch: SettingsPatch): Promise<SettingsState> => patchSettings(patch),
      onChanged: (fn: (s: SettingsState) => void) => onSettingsChanged(fn),
    },

    spelling: {
      /**
       * The browser and the system own the dictionaries; the page can only
       * say which language the text is in. Offered: the user's own languages.
       */
      languages: async (): Promise<{ available: string[]; current: string[] }> => {
        const available = [...new Set(navigator.languages ?? [navigator.language])]
        const chosen = getSettings().editor.spellcheckLanguage
        return { available, current: [chosen ?? navigator.language] }
      },
      setLanguage: async (lang: string | null): Promise<SettingsState> => {
        const valid = lang && /^[a-zA-Z]{2,3}(-[a-zA-Z0-9]{2,8})*$/.test(lang) ? lang : null
        applyLanguage(valid)
        return patchSettings({ editor: { spellcheckLanguage: valid } })
      },
    },

    themes: {
      list: (): Promise<ThemeInfo[]> => listThemes(),
      read: (id: string): Promise<string> => readTheme(id),
      onChanged: (fn: () => void) => onThemesChanged(fn),
    },

    window: {
      minimize: (): void => {},
      captionColours: (_color: string, _symbolColor: string): void => {},
      toggleMaximize: (): void => {},
      close: (): void => {},
      /** A new tab of the app: the browser's counterpart of a new window. */
      newWindow: (): void => {
        window.open(`${location.origin}/`, '_blank', 'noopener')
      },
      setAlwaysOnTop: (_on: boolean): void => {},
      toggleFullscreen: (): void => {
        if (document.fullscreenElement) void document.exitFullscreen()
        else void document.documentElement.requestFullscreen?.().catch(() => {})
      },
      toggleDevTools: (): void => {},
      setSpellcheck: (enabled: boolean): void => {
        document.documentElement.spellcheck = enabled
      },
      onState: (fn: (s: WindowState) => void) => subscribe(focusListeners, fn),
      /** Closing a tab is the browser's, asked through beforeunload (App.vue). */
      onCloseRequest: (_fn: () => void) => () => {},
      replyClose: (_allow: boolean): void => {},
      ackClose: (): void => {},
    },

    app: {
      /** Web links and mail only, in a new tab that cannot reach back into this one. */
      openExternal: async (url: string): Promise<void> => {
        if (!/^(https?:\/\/|mailto:)/i.test(url)) return
        window.open(url, '_blank', 'noopener,noreferrer')
      },
      version: async (): Promise<string> => __APP_VERSION__,
      clearRecent: async (): Promise<void> => {
        patchSettings({ recentFiles: [] })
      },
      confirm: async (
        message: string,
        detail?: string,
        ok = 'OK',
        cancel = 'Cancel'
      ): Promise<boolean> =>
        (await ask({
          kind: 'question',
          message,
          detail,
          buttons: [ok, cancel],
          defaultId: 0,
          cancelId: 1,
        })) === 0,
      info: (message: string, detail?: string): Promise<void> => info(message, detail),
      /** Kept in the page for the session; never sent anywhere. */
      logError: (message: string): void => {
        errorLog.push(message)
        if (errorLog.length > 200) errorLog.shift()
        console.error(message)
      },
      about: async (): Promise<void> => {
        await ask({
          message: 'ekram.md',
          detail: `Version ${__APP_VERSION__}\n\nA markdown editor that runs entirely in your browser. Files you open stay on your device and are never uploaded.`,
          buttons: ['OK'],
          cancelId: 0,
          icon: '/icon.png',
        })
      },
      systemDark: async (): Promise<boolean> => darkQuery?.matches ?? false,
      onSystemDarkChanged: (fn: (dark: boolean) => void) => subscribe(darkListeners, fn),
    },

    clipboard: {
      write: (data: { text: string; html?: string }): Promise<void> => writeClipboard(data),
    },

    /** What only a browser needs. */
    web: {
      /** Writes unsaved work to the journal now and waits for it: before a reload on purpose. */
      flushJournals: (): Promise<void> => flushJournalsAndWait(),
      /** Whether files can be written where they are, rather than downloaded. */
      fileSystemAccess: hasFileSystemAccess,
      /** A path as a person should read it, without its internal id. */
      displayPath,
      /** Those of `paths` the browser wants a click before the page may read. */
      needingPermission: async (paths: string[]): Promise<string[]> => {
        const out: string[] = []
        for (const p of paths) if (!(await permitted(p))) out.push(p)
        return out
      },
      /**
       * Asks for read and write access to the files and folders behind `paths`.
       * Must run from a click. Resolves to the paths still not allowed.
       */
      requestAccess: async (paths: string[]): Promise<string[]> => {
        const asked = new Map<string, boolean>()
        const refused: string[] = []
        for (const path of paths) {
          const p = parsePath(path)
          if (!p || (p.scheme !== 'ws' && p.scheme !== 'fh')) continue
          if (!asked.has(p.id)) {
            const rec = await handleRecord(p.id)
            let ok = !rec
            if (rec) {
              try {
                await ensurePermission(rec.handle, 'readwrite')
                ok = true
              } catch {
                ok = false
              }
            }
            asked.set(p.id, ok)
          }
          if (!asked.get(p.id)) refused.push(path)
        }
        return refused
      },
      /** Starts reading a drop; call it inside the drop event. */
      readDrop: (dt: DataTransfer, wanted: (name: string) => boolean): Promise<Dropped[]> =>
        readDrop(dt, wanted),
      dropHasOpenable: (dt: DataTransfer, wanted: (name: string) => boolean): boolean =>
        dropHasOpenable(dt, wanted),
      /** Opens files read without a handle, as copies. */
      openCopies: (files: File[]): Promise<DocumentFile[]> => openCopies(files),
      openDroppedFolder: (d: Extract<Dropped, { kind: 'folder' | 'snapshot' }>): Promise<string> =>
        openDroppedFolder(d.kind === 'folder' ? d.handle : { name: d.name, files: d.files }),
      /** Reopens a folder after its permission was given again. */
      reopenFolder: (root: string): Promise<string> => openFolderAt(root),
      /** Asks for a CSS file and imports it as a theme; resolves to its id, or null. */
      importTheme: async (): Promise<string | null> => {
        const files = await pickWithInput({ accept: '.css,text/css' })
        const file = files?.[0]
        if (!file) return null
        return importTheme(file.name, await file.text())
      },
      removeTheme: (id: string): Promise<void> => removeTheme(id),
      /** Where the menu's Undo, Cut, Paste and the rest are carried out. */
      onEditAction: (fn: (action: EditAction) => void): void => {
        editHandler = fn
      },
      /** The URL an image link shows as: see images.ts. */
      resolveImage: (src: string, documentDir: string | null): Promise<string> =>
        resolveImage(src, documentDir),
      addRecentFile,
      version: __APP_VERSION__,
    },
  }
}

export type Api = ReturnType<typeof createApi>

declare global {
  interface Window {
    api: Api
  }
}

/** The page's language hint for spelling. */
function applyLanguage(lang: string | null): void {
  if (lang) document.documentElement.lang = lang
  else document.documentElement.lang = 'en'
}

/** Installs `window.api` and starts what runs for the page's whole life. */
export function installApi(): void {
  window.api = createApi()
  void claimTab()
  initWorkspace()
  initLaunchQueue()
  const s = getSettings()
  applyLanguage(s.editor.spellcheckLanguage)
  document.documentElement.spellcheck = s.editor.spellcheck

  // A hidden tab may be discarded without another chance: unsaved text goes
  // to the journal now rather than after its debounce.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushJournals()
  })
  window.addEventListener('pagehide', flushJournals)

  // Recovery data must not be evicted when the disk is short of space.
  void navigator.storage?.persist?.().catch(() => false)

  void pruneHandles([
    ...s.recentFiles,
    ...s.session.files,
    ...(s.workspace ? [s.workspace] : []),
  ]).catch(() => {})
}
